import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Compute the base URL for serving assets and routing.
 * Priority:
 * 1) VITE_APP_BASE_URL if provided (e.g. "/business")
 * 2) Detect a "/business" segment anywhere in the current pathname (handles locale prefixes like "/uae/en/business")
 * 3) Otherwise, if on a configured root domain, use VITE_ROOT_BASE_URL or "/business"
 * 4) Fallback to "/"
 */
export function getBaseUrl(): string {
  const envBase = (import.meta as any).env?.VITE_APP_BASE_URL;
  if (envBase) return envBase;
  if (typeof window !== 'undefined') {
    const p = window.location.pathname || '';
    // If the path contains "/business" anywhere (e.g. "/uae/en/business/chronicle"),
    // return the prefix up to and including "/business" so asset URLs resolve correctly.
    const m = p.match(/^(.*?)(\/business)(?:\/|$)/);
    if (m) {
      const prefix = m[1] || '';
      return `${prefix}/business`;
    }
  }
  if (typeof isRootDomain === 'function' && isRootDomain()) {
    return (import.meta as any).env?.VITE_ROOT_BASE_URL || '/business';
  }
  return '/';
}

/**
 * Build the absolute path for static assets.
 * Example: getAssetsUrl("/icons/x.svg") >>> "/bip/assets/icons/x.svg"
 */
export function getAssetsUrl(path: string): string {
  return `/bip/assets${path}`;
}

/**
 * Build a public URL to files in /public.
 * - Ensures a leading slash for the input path
 * - In production, prefixes with the detected base (e.g. "/uae/en/business") so assets resolve under locale + app base
 * - In dev, return a root-absolute path so Vite serves from "/"
 * Example: getPublicUrl('Logos/chronicle/Heading.png')
 *   - Dev: "/Logos/chronicle/Heading.png"
 *   - Prod (on /uae/en/business/*): "/uae/en/business/Logos/chronicle/Heading.png"
 */
export function getPublicUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  const isDev = (import.meta as any).env?.DEV;

  // Public dir assets (Logos, backgrounds, images) are copied to dist root.
  // In dev, they are served from "/". In preview/prod with a base (e.g. "/business"),
  // they must be referenced with the base prefix to avoid history-fallback 404s.
  if (/^\/(Logos|backgrounds|images)\//i.test(p)) {
    if (isDev) return p; // dev server serves from root
    const base = getBaseUrl() || (import.meta as any).env?.BASE_URL;
    return base && base !== '/' ? `${base}${p}` : p;
  }

  if (isDev) return p;
  const base = getBaseUrl() || (import.meta as any).env?.BASE_URL;
  return base && base !== '/' ? `${base}${p}` : p;
}


/**
 * Convenience helper to build an image URL from the assets folder.
 * Example: getImageUrl("logo.png") >>> "/bip/assets/images/logo.png"
 */
export function getImageUrl(filename: string): string {
  return getAssetsUrl(`/images/${filename}`);
}

/**
 * Parse a comma-separated domain list from an ENV variable into a array[string].
 * Removes whitespace and ignores empty entries.
 */
function parseDomains(envVar: string | undefined): string[] {
  if (!envVar) return [];
  return envVar
    .split(",")
    .map(d => d.trim())
    .filter(Boolean);
}

/**
 * Get the list of root domains (from ENV `VITE_ROOT_DOMAINS`).
 */
export function getRootDomains(): string[] {
  return parseDomains(import.meta.env.VITE_ROOT_DOMAINS);
}

/**
 * Get the list of tenant domains (from ENV `VITE_TENANT_DOMAINS`).
 */
export function getTenantDomains(): string[] {
return parseDomains(import.meta.env.VITE_TENANT_DOMAINS);
}

/**
 * Return the current hostname from `window.location.hostname`.
 */
export function getCurrentHost(): string {
  return window.location.hostname;
}

/**
 * Check if the current host matches any root domain.
 */
export function isRootDomain(): boolean {
  return getRootDomains().includes(getCurrentHost());
}

/**
 * Check if the current host matches any tenant domain.
 */
export function isTenantDomain(): boolean {
  return getTenantDomains().includes(getCurrentHost());
}

/**
 * Validate whether a given domain belongs to either root or tenant domains.
 */
export function isDomainValid(domain: string): boolean {
  if (!domain) return false;
  return (
    getRootDomains().includes(domain) ||
    getTenantDomains().includes(domain)
  )
}

/**
 * Extract subdomain from current host
 * Example:
 *   audits.yc.local   -> "audits"
 *   audits.localhost  -> "audits"
 *   yc.local          -> null (no subdomain)
 *   localhost         -> null (no subdomain)
 */
export function getSubdomain(): string | null {
  const host = getCurrentHost();
  if (!host) return null;

  const parts = host.split(".");

  // Handle localhost cases like subdomain.localhost
  const isLocalhost = host.endsWith("localhost") && parts.length > 1;

  // Handle normal domain with at least 3 parts (subdomain.domain.tld)
  const isMultiPartDomain = parts.length >= 3;

  if (isLocalhost || isMultiPartDomain) {
    return parts[0] || null;
  }

  return null;
}

/**
 * Simple wrapper for internal links.
 * - When previewing a tenant via path (e.g. /site/:sub), prefix links with that segment
 *   so navigation stays inside the tenant instance.
 */
export function linkTo(path: string): string {
  const p = path.startsWith('/') ? path : `/${path}`;
  // Detect path-based tenant preview. Window path may include the router basename (e.g. /business)
  const current = typeof window !== 'undefined' ? window.location.pathname : '';
  const match = current.match(/(?:^|\/)(?:business)?\/site\/([^/]+)/);
  if (match) {
    const prefix = `/site/${match[1]}`;
    if (p.startsWith(prefix + '/')) return p;     // already prefixed
    if (p === prefix) return p;                    // exact
    if (p.startsWith('/site/')) return p;          // linking explicitly to a site path
    return `${prefix}${p}`;
  }
  return p;
}
